import React, {memo} from "react"
import styles from "./../stylesheets/typography.module.scss"
import {classNames} from "scholastic-client-components/src/SharedPackages/classnames"

type TermsOfUseProps = {
    className?: string
    smallerTitle?: boolean
}

export const TermsOfUse: React.FC<TermsOfUseProps> = memo(({className, smallerTitle}) => {
    return (
        <article className={className}>
            <div className={styles.header}>
                <h1 className={classNames(styles.h1, smallerTitle && styles.h1_smaller)}>
                    Terms of Use
                </h1>
            </div>
            <h3 className={styles.h3}>
                <strong>EFFECTIVE DATE: January 1</strong>
            </h3>
            <p className={styles.p}>
                Thank you for choosing online products and services owned and operated by TSN, LLC.
                (hereafter called &quot;<strong>TSN</strong>&quot;). These{" "}
                <strong>Terms of Service</strong> (these &quot;<strong>Terms</strong>&quot;) govern
                all access to and use of online and/or mobile services, websites, software, and
                content assets (images, videos, stickers, themes, etc.) provided on or in connection
                with the following list (collectively, the &quot;<strong>Services</strong>&quot;):
            </p>
            <ul className={styles.ul}>
                <li className={styles.li}>
                    <a target={"_blank"} href={"http://www.tsnvcs.com"} rel="noreferrer">
                        www.tsnvcs.com
                    </a>
                </li>
                <li className={styles.li}>
                    <a
                        target={"_blank"}
                        href={"https://portal.thescholasticnetwork.com"}
                        rel="noreferrer"
                    >
                        portal.thescholasticnetwork.com
                    </a>
                </li>
                <li className={styles.li}>
                    <a target={"_blank"} href={"https://portal.tsnvcs.com"} rel="noreferrer">
                        portal.tsnvcs.com
                    </a>
                </li>
            </ul>
            <p className={styles.p}>
                These <strong>Terms</strong> also govern any and all applications associated with
                the above or any other TSN website, app, or online service that links to these
                Terms.
            </p>
            <p className={styles.p}>
                Your access to and use of the Services is conditioned on your acceptance of and
                compliance with these Terms. These Terms apply to anyone (&quot;<strong>you</strong>
                &quot;) who accesses or uses the Services. If you open a TSN account on behalf of a
                school, school district, higher education institution, business, or any other
                organization, then you are agreeing to these Terms for that organization and
                promising to TSN that you have the authority to bind that organization to these
                Terms (in which case, &quot;
                <strong>you</strong>&quot; and &quot;<strong>your</strong>&quot; will refer to that
                organization). By accessing or using any of the Services, initiating a trial, or by
                clicking a button or checking a box marked &quot;I agree&quot; (or something
                similar), you signify that you have read, understood, and agree to be bound by these{" "}
                <strong>Terms</strong>, and to the collection and use of your information as set
                forth in our <strong>Privacy Policy</strong> (available at{" "}
                <a
                    target={"_blank"}
                    href={"https://portal.thescholasticnetwork.com/auth/privacy-policy"}
                    rel="noreferrer"
                >
                    https://portal.thescholasticnetwork.com/auth/privacy-policy
                </a>
                , the &quot;
                <strong>Privacy Policy</strong>&quot;), whether or not you are a registered user of
                the Services.
            </p>
            <p className={styles.p}>
                If you are an organization, then you may authorize individuals to access and use the
                Services through your TSN account (each, a &quot;<strong>User</strong>&quot;) as
                part of your subscription to the Services (&quot;<strong>Your Subscription</strong>
                &quot;). You are responsible for ensuring that only authorized Users access the
                Services through your TSN account, you are solely responsible for all activity
                conducted through your TSN account, and, as between you and TSN, you are solely
                responsible for any violation of these Terms by any of your Users or anyone else who
                accesses the Services through your TSN account. Furthermore, you acknowledge and
                agree that your Users will be subject to these Terms, the Privacy Policy, and any
                other posted guidelines, policies or rules applicable to specific features of the
                Services, which may be posted from time to time on any of the Services
                (collectively, the &quot;
                <strong>Guidelines</strong>&quot;), which are hereby incorporated by reference.
            </p>
            <p className={styles.p}>
                TSN reserves the right to modify these Terms, the Privacy Policy, and the Guidelines
                at any time. If there are any inconsistencies between the Terms and any of the
                Guidelines, these Terms shall prevail.
            </p>
            <p className={styles.p}>
                <strong>A note about Student Data:</strong> The Services may be purchased by
                providers of educational services, such as schools, school districts, teachers,
                higher education institutions, faculty, and staff (collectively referred to as
                &quot;Schools&quot; or individually as a &quot;<strong>School</strong>&quot;) that
                use our services for educational purposes. If you are a School, then your Users will
                include teachers, administrators, and students. When TSN contracts with a School to
                provide the Services, we may collect or have access to Student Data (defined below),
                which may be provided by the School or by student Users. We consider such Student
                Data to be strictly confidential and in general do not use Student Data for any
                purpose other than improving and providing our Services to the School or on the
                School&apos;s behalf. Our collection, use, and sharing of Student Data is governed
                by these Terms and any applicable laws and regulations including, in the U.S.,
                provisions of the Family Educational Rights and Privacy Act (&quot;
                <strong>FERPA</strong>&quot;), the Children&apos;s Online Privacy Protection Act
                (&quot;<strong>COPPA</strong>&quot;) and applicable state laws. As used in these
                Terms, &quot;<strong>Student Data</strong>&quot; means any personal information that
                is directly related to a student User, which may include &quot;educational
                records&quot; as defined by FERPA.
            </p>
            <p className={styles.p}>
                <strong>A note about Employee Data:</strong> TSN recognizes that individuals under
                the age 18 may be employed by or otherwise connected to a purchasing organization
                and that the purchasing organization may want to provide employees or other
                individuals with access to Services. When TSN contracts with an entity to provide
                the Services, we may collect or have access to Individual or Employee Data (defined
                below), which may be provided by the purchasing entity or by the employee. We
                consider such Employee Data to be strictly confidential and in general do not use
                Employee Data for any purpose other than improving and providing our Services to the
                purchasing organization or on the organization&apos;s behalf. Our collection, use
                and sharing of Individual and/or Employee Data is governed by these Terms and any
                applicable laws and regulations including, in the U.S., provisions of FERPA, COPPA,
                The Americans with Disabilities Act (&quot;<strong>ADA</strong>&quot;), and
                applicable state laws. As used in these Terms, &quot;<strong>Employee Data</strong>
                &quot; means any personal information that is directly related to an employee of
                yours, which may include personally identifiable information (&quot;
                <strong>PII</strong>&quot;) such as an employee&apos;s name, email address, or other
                contact information.
            </p>
            <p className={styles.p}>
                <strong>
                    PLEASE READ THESE TERMS CAREFULLY TO ENSURE THAT YOU UNDERSTAND EACH PROVISION.
                    THESE TERMS CONTAINS A MANDATORY INDIVIDUAL ARBITRATION AND CLASS ACTION/JURY
                    TRIAL WAIVER PROVISION THAT REQUIRES THE USE OF ARBITRATION ON AN INDIVIDUAL
                    BASIS TO RESOLVE DISPUTES, RATHER THAN JURY TRIALS OR CLASS ACTIONS.
                </strong>
            </p>

            <h2 className={styles.h2}>
                1. <u>The Services.</u>
            </h2>
            <p className={styles.p}>
                The Services includes a visual communication ecosystem (digital signage software
                system) (the &quot;<strong>Network</strong>&quot;) that allows Users to create,
                schedule, manage, and distribute visual content to screens/displays at locations
                where you have authority and/or permission to broadcast that visual content. Unless
                explicitly stated otherwise, any new or improved features to the Services shall be
                provided subject to these Terms.
            </p>
            <p className={styles.p}>
                The <strong>Network</strong> includes three types of libraries of content and
                information (&quot;<strong>Content Libraries</strong>&quot;):
                <ul className={styles.ul}>
                    <li className={styles.li}>
                        Local Libraries: Local libraries store visual communication assets and other
                        files (images and videos) that you or your Users create (&quot;
                        <strong>User Content</strong>&quot;).
                    </li>
                    <li className={styles.li}>
                        Global Libraries: Global libraries store visual communication assets and
                        other files that are created, purchased, or otherwise made available by TSN
                        (each, a &quot;<strong>Global Library</strong>&quot;).
                    </li>
                    <li className={styles.li}>
                        Premium Libraries: Premium libraries store visual communication content from
                        third party entities that are pre-approved by TSN (each, a &quot;
                        <strong>Premium Library</strong>&quot;).
                    </li>
                </ul>
            </p>
            <h4 className={styles.h4}>
                A. License to Use the Services (Software & Digital Content)
            </h4>
            <p className={styles.p}>
                <strong>Software License.</strong> TSN grants you a non-transferable, non-exclusive
                right and license to use the Services and all software that is owned by TSN and made
                available to you as part of Your Subscription (the &quot;<strong>Software</strong>
                &quot;). You agree that you will not copy, modify, create a derivative work of,
                reverse engineer, reverse assemble or otherwise attempt to discover any source code,
                sell, assign, sublicense, grant a security interest in or otherwise transfer any
                right to the Software. You agree not to modify the Software in any manner or form,
                or to use modified versions of the Software, including (without limitation) for
                obtaining unauthorized access to the Services. You agree not to access the Services
                by any means other than through the interface that is provided by TSN for such
                purpose.
            </p>
            <p className={styles.p}>
                <strong>TSN Global Content License.</strong> TSN grants you a non-transferable,
                non-exclusive right and license to use the content made available to you, as part of
                Your Subscription, through the Global Library inside the Network (&quot;
                <strong>TSN Global Content</strong>&quot;). You agree that you will only use TSN
                Global Content assets (images, videos, stickers, themes, etc.) for broadcast via the
                Services. You agree that you will not copy, modify, create a derivative work, sell,
                assign, sublicense, grant a security interest in or otherwise transfer any right to
                any of the TSN Global Content.
            </p>
            <p className={styles.p}>
                <strong>Premium Content License.</strong> Access to the Network includes the option
                (fees may apply) to access content within the Premium Library inside the Network
                (&quot;<strong>TSN Premium Content</strong>&quot;) as part of Your Subscription. The
                Premium Library holds content from third-party providers that have been vetted by
                TSN, and such third-party content providers have given TSN the rights to offer you
                the non-transferable, non-exclusive right to use such TSN Premium Content (subject
                to additional fees, where applicable). If you opt in to use TSN Premium Content and
                pay all applicable fees, then TSN grants you a limited license to use TSN Premium
                Content through the Network as part of Your Subscription. You agree that you will
                not copy, modify, create a derivative work, sell, assign, sublicense, grant a
                security interest in or otherwise transfer any right to any TSN Premium Content. You
                agree that you will only use TSN Premium Content for broadcast via the Network.
            </p>
            <h4 className={styles.h4}>B. Intellectual Property</h4>
            <p className={styles.p}>
                You acknowledge and agree that the Services and Software contain proprietary and
                confidential information that is protected by applicable intellectual property and
                other laws. You further acknowledge and agree that information (including graphical
                information such as images, videos, stickers, themes/templates) presented to you
                through the Services is protected by copyrights, trademarks, service marks, patents
                or other proprietary rights and laws. Except as expressly authorized by TSN, you
                agree not to copy, download, modify, rent, lease, loan, sell, distribute outside of
                the Network, disassemble, or create derivative works based on the Services, in whole
                or in part. Any automated scraping, harvesting, indexing, mining, or any other
                extraction of any content from the Services is expressly prohibited.
            </p>
            <p className={styles.p}>
                The Services are protected by copyright and other laws in both the United States and
                elsewhere. You must not distribute or reproduce TSN Global Content, TSN Premium
                Content, or any other content within the Network that was not created by you or any
                of your Users, in whole or in part, by any means, including but not limited to
                electronic and print.
            </p>
            <p className={styles.p}>
                TSN reserves the right to cancel your account without refund if it is determined
                that you or any of your Users has violated these Terms.
            </p>
            <h4 className={styles.h4}>C. Copyright Protection</h4>
            <p className={styles.p}>
                TSN recognizes the importance of the Digital Millennium Copyright Act
                (&quot;DMCA&quot;) and takes alleged copyright infringement reports very seriously.
                TSN reserves the right to remove allegedly infringing content without notice.
                Furthermore, TSN reserves the right to promptly terminate the account of any User
                under any circumstances TSN deems appropriate, including a single instance of
                allegedly infringing behavior by the User.
            </p>
            <h4 className={styles.h4}>D. Restrictions on Use</h4>
            <p className={styles.p}>
                You and your Users shall not:
                <ul className={styles.ul}>
                    <li className={styles.li}>
                        except as expressly permitted by these Terms, license, sell, rent, lease,
                        loan, assign, host, use, store, copy, reproduce, modify, translate, publish,
                        broadcast, transmit or otherwise commercially exploit the Services or any
                        component thereof;
                    </li>
                    <li className={styles.li}>
                        except as expressly permitted by these Terms, distribute, make available, or
                        sell access to the Services, or any component thereof, to any third parties;
                    </li>
                    <li className={styles.li}>
                        reverse engineer, decompile, disassemble, or otherwise attempt to derive the
                        source code for any of the Services, Software, or components thereof; or
                    </li>
                    <li className={styles.li}>
                        circumvent, disable or otherwise interfere with security-related features or
                        features that prevent or restrict use or copying of any User Content or the
                        Services, or any component thereof, or enforce limitations on use of the
                        Services, User Content, TSN Services and Software, or any component thereof.
                    </li>
                </ul>
            </p>
            <h4 className={styles.h4}>E. Changes to the Services</h4>
            <p className={styles.p}>
                TSN may implement changes and improvements to the Services including but not limited
                to conducting maintenance, updating functionality or look and feel of the Services,
                and adding or removing features, content assets, and functionality. We may also
                pause or stop providing Services, set feature limits or restrict access to you, your
                organization or your Users to all or a part of the Services at any time without
                liability.
            </p>
            <p className={styles.p}>
                TSN expects and intends the Services to be generally available. However, during
                times of updates/upgrades and routine or periodic maintenance, the Services may be
                unavailable. You agree that TSN shall not be liable to you, your users, or any third
                party for any modification, suspension, or termination of the Services or their
                components.
            </p>

            <h2 className={styles.h2}>
                2. <u>Eligibility and Authority</u>
            </h2>
            <p className={styles.p}>
                If you are an adult over the age of 18, you may purchase the Services as an
                individual. If you are an adult over the age of 18 with the authority granted to you
                by your organization to purchase the Services <u>and</u> agree to these Terms on
                behalf of your organization, you may purchase the Services on behalf of your
                organization. Under no circumstances may you purchase the Services if you are under
                the age of 18.
            </p>
            <p className={styles.p}>
                The Services are unique in that individuals under the age of 18 may be viewers of
                content that schools, businesses, universities, and other organizations that
                distribute content to displays/screens via the Services. Distribution of viewable
                content through the Services is controlled by the purchasing entity. Visibility of
                the content distributed through the Services does not require the viewer to have a
                login or share any PPI.
            </p>
            <p className={styles.p}>
                COPPA requires that online service providers obtain verifiable parental consent
                before collecting personal information from children under 13. TSN relies on each
                end-user entity (schools, etc.) to obtain and provide appropriate consent and
                disclosures, if necessary, for TSN to collect any Student Data, including the
                collection of Student Data directly from students under 13, as permitted under
                COPPA. If you are a School or any other entity and you allow children under the age
                of 13 to access the Services through your TSN account, then you represent and
                warrant to TSN that you have received consent from parents, or have the authority to
                provide consent on behalf of parents, for us to collect information from students
                before allowing children under 13 to access the Services.
            </p>

            <h2 className={styles.h2}>
                3. <u>Service Usage Obligations</u>
            </h2>
            <p className={styles.p}>
                In exchange for access to the Services, you agree to (a) provide true, accurate,
                current, and complete information about yourself and your organization as prompted
                by the Services, and (b) maintain and keep all of your data true, accurate, current,
                and complete. If you provide incorrect, incomplete, or inaccurate information, or
                give TSN reasonable grounds to suspect that your information is untrue, inaccurate,
                not current, or incomplete, TSN has the right to suspend or terminate your current
                or future access to the Services (in whole or in part).
            </p>

            <h2 className={styles.h2}>
                4. <u>License Types</u>
            </h2>
            <p className={styles.p}>
                TSN sells, both directly and indirectly through a reseller network, access to the
                Services in the form of licenses. Each license is provided for a specific length of
                time and a specific price (collectively the &quot;<strong>License Terms</strong>
                &quot;). License Terms are specified on the purchase document which is executed by
                you and either the reseller or TSN.
            </p>
            <p className={styles.p}>
                <strong>License Types:</strong>
            </p>
            <ul className={styles.ul}>
                <li className={styles.li}>
                    <strong>Device License:</strong> A device license allows you to distribute
                    content through the Network to one screen/display. For example, if you have 10
                    TVs that you want to broadcast to via the purchased Services, you need one
                    license for each of the 10 screens for a total of 10 licenses. You may place
                    these screens/displays anywhere you choose.
                </li>
                <li className={styles.li}>
                    <strong>Site License:</strong> A site license allows you to distribute content
                    through the Network to as many screens/displays as you want provided that those
                    screens are all at one geographic location (one campus, one company building,
                    etc.). Because the Network includes the option of broadcasting content to
                    community organizations that support specific locations (the Community Connect
                    module within the Network), a screen/display that is managed by your site but is
                    located in a community organization is considered a part of your geographic
                    location.
                    <ul className={styles.ul}>
                        <li className={styles.liCircle}>
                            <strong>Education Example:</strong> A school district purchases five
                            site licenses, one for each of its four schools and one for the
                            administration offices. Each school is partnered with a local community
                            based organization and has also secured permission to put screens in
                            restaurants and coffee shops close to its campus. The screens in the
                            restaurants and coffee shops do not require a separate license, as they
                            are considered part of the site licenses for the individual schools.
                        </li>
                        <li className={styles.liCircle}>
                            <strong>Business Example:</strong> A local business operates out of one
                            location. This business often works with the local Jobs Center to
                            recruit employees. The business purchased a site license for the
                            Services. Even though the community organization is not at the same
                            physical address, the site license includes Community Connect and the
                            business can put screens/displays at the Job Center without the need for
                            an additional license.
                        </li>
                    </ul>
                    As a service to the license holder and its local community, TSN includes the
                    Community Connect module in all site license purchases for no additional cost.
                    This part of the site license may only be used to broadcast externally into
                    local community organizations. It is in no way a means to circumvent purchasing
                    additional site licenses. TSN reserves the right to adjust, terminate, or reduce
                    access to the Services if it becomes aware of such circumvention. This
                    adjustment, termination, or reduction in access may happen with or without
                    notice based on TSN&apos;s sole discretion.
                </li>
            </ul>

            <h2 className={styles.h2}>
                5. <u>Pricing, Purchases, and Payments</u>
            </h2>
            <h4 className={styles.h4}>A. Quotes & Proposals</h4>
            <p className={styles.p}>
                Any quotes or proposals provided by TSN are valid only for a limited time and are
                effective only with the agreement of the relevant parties. Quotes and proposals may
                be written by TSN at any time in its sole discretion. Quotes and proposals may
                include proprietary and confidential information and to the maximum extent permitted
                by law may not be disclosed to anyone other than their intended recipients. By
                requesting and/or accepting receipt of a quote or proposal from TSN, you agree to
                keep such quotes or proposals confidential, not disclose them to any third party,
                and to return or destroy them upon receiving a request to do so from TSN. If public
                records laws apply to a quote or proposal, you agree to immediately notify TSN of
                any public record request that may result in disclosure of a TSN quote or proposal
                and to allow TSN to take reasonable steps to prevent that disclosure to the maximum
                extent allowed by law.
            </p>
            <h4 className={styles.h4}>B. Pricing</h4>
            <p className={styles.p}>
                TSN reserves the right to adjust the pricing for the Services in any manner and at
                any time in our sole and absolute discretion.
            </p>
            <h4 className={styles.h4}>C. Purchases through Authorized Resellers (Third-Parties)</h4>
            <p className={styles.p}>
                The Services may be purchased through a TSN partner and/or their reseller network.
                If you purchase the Services through a TSN partner or an authorized reseller,
                portions of this Section may not apply to you. Because a purchase of the Services in
                this manner is between you and the third-party, and not TSN, you acknowledge and
                agree that TSN is not responsible for billing for your purchase and is not
                responsible or liable for any claims relating to the billing of your purchase. If
                you have questions about your purchase or billing, reach out to your reseller
                directly.
            </p>
            <h4 className={styles.h4}>D. Payments and Taxes</h4>
            <p className={styles.p}>
                If you order the Services directly from TSN, you must pay the fees for the Services
                in accordance with the payment terms stated in the applicable ordering document
                between you and TSN (&quot;<strong>Order Form</strong>&quot;). You have the option
                to make payment by check or other methods at TSN&apos;s discretion.
            </p>
            <p className={styles.p}>
                Payments must be received by TSN no later than 30 days after TSN issues an invoice.
                If TSN does not receive payment within 30 days, the invoice is past due and TSN
                reserves the right to suspend access to the affected license(s) and take collection
                action. Suspension of an account does not relieve you of the obligation to pay for
                the purchase. TSN reserves the right to impose a finance charge on past-due payments
                in an amount equal to 1.5% per month or the highest percentage permitted by
                applicable law, whichever is less. In addition, you agree to reimburse TSN for all
                expenses that TSN incurs in exercising any of its rights under these Terms or
                applicable law with respect to a default in payment by you, including reasonable
                attorney fees and/or the fees of any collection agency retained by TSN.
            </p>
            <p className={styles.p}>
                You are responsible for, and will pay or reimburse TSN for, all federal, state, and
                local taxes, including sales, use, VAT, and similar governmental charges and taxes
                that are imposed on your purchase of the Services (&quot;
                <strong>Sales Taxes</strong>&quot;), unless you provide TSN with a valid tax
                exemption certificate at the time you sign your Order Form. If TSN is required to
                collect Sales Taxes from you and remit them to a taxing authority, TSN will
                separately state the Sales Taxes on an invoice to be paid by you.
            </p>
            <h4 className={styles.h4}>E. Cancellations & Renewals</h4>
            <p className={styles.p}>
                All cancellations requested before the end of the then-current term will be
                effective at the end of the current term. Payments for the Services made prior to
                cancellation of the Services will not be refunded. When a license term expires
                without a renewal, TSN reserves the right to immediately terminate the access to the
                expired license(s). TSN also reserves the right to extend the license(s) for a
                limited time after the conclusion of the term. If your license expires and the
                access is terminated, TSN is not liable for any loss of data or content held within
                your license(s), including both User data and digital assets.
            </p>

            <h2 className={styles.h2}>
                6. <u>User Accounts, Passwords, & Security</u>
            </h2>
            <p className={styles.p}>
                TSN uses usernames and passwords to authenticate access to the Services. You are
                responsible for ensuring that only authorized Users access your TSN account, for
                preventing unauthorized access to the Services through your TSN account, and for all
                activity that happens on or through Your TSN account.
            </p>
            <p className={styles.p}>
                TSN allows unlimited User accounts for each license provided that (i) each User must
                have a unique username and password, and (ii) Users may not share their usernames or
                passwords. You are responsible for ensuring that your Users maintain the
                confidentiality of their login information. You agree to (a) notify TSN immediately
                of any unauthorized use of any User’s login information or any unauthorized access
                to the Services through your TSN account, and (b) ensure that each User exists from
                your TSN account (logout) at the end of each session. TSN cannot and will not be
                liable for any unauthorized access to your TSN account or data that arises from your
                acts or omissions.
            </p>

            <h2 className={styles.h2}>
                7. <u>User Data</u>
            </h2>
            <h4 className={styles.h4}>A. Compliance with Laws</h4>
            <p className={styles.p}>
                When the Services are used by educational institutions, TSN may store Student Data
                that is provided by the School or by the students. Your organization or the student,
                not TSN, owns and controls the Student Data. You authorize TSN to access, collect,
                transmit, modify, display and store Student Data to provide the Services as
                described in these Terms and in our Privacy Policy.
            </p>
            <p className={styles.p}>
                In the U.S., TSN may collect and process Student Data as a School Official with a
                legitimate educational interest pursuant to FERPA, 20 U.S.C. § 1232(g). Individually
                and collectively, we and our educational institution license holders agree to uphold
                our obligations under FERPA, COPPA, the Protection of Pupil Rights Amendment,
                applicable state laws relating to student data privacy, and with all other laws and
                regulations governing the protection of Student Data.
            </p>
            <p className={styles.p}>
                When the Services are used by businesses, TSN may store Employee Data that is
                provided by the business or the end user. You are responsible for the level of
                Employee Data, including any PII that you and your employees enter into the
                Services. TSN does not require the use of sensitive PII in the operation or use of
                the Services.
            </p>
            <h4 className={styles.h4}>B. Use of Data</h4>
            <p className={styles.p}>
                By submitting, providing TSN access to, or causing us to receive Student Data or
                Employee Data, you agree that TSN may use such data for the purposes of (i)
                providing the Services, (ii) improving and developing the Services, (iii) enforcing
                our rights under these Terms, and (iv) as permitted by and through your or the
                applicable User’s consent.
            </p>
            <h4 className={styles.h4}>C. Use of De-Identified or Anonymized Data</h4>
            <p className={styles.p}>
                You agree that both during and after the term of the License, TSN may collect,
                analyze, use and retain data derived from end User data, including but not limited
                to how and when a User accesses and uses the Services. This data may be used to
                operate, analyze, improve or market the Services, develop new products or services,
                conduct research, or other purposes. TSN will not share or publicly disclose
                information derived from User data unless that data is de-identified and or
                anonymized to the point that it cannot reasonably identify a specific person.
            </p>
            <h4 className={styles.h4}>
                D. Use of Personal Information for Marketing & Communication
            </h4>
            <p className={styles.p}>
                To better serve users of the TSN Services, you agree that TSN may provide customized
                content, commercial messaging, system updates, product releases, and advertising to
                Users from time-to-time, as permitted by law.
            </p>
            <h4 className={styles.h4}>E. Disclosure of Data and Third-Party Service Providers</h4>
            <p className={styles.p}>
                You acknowledge and agree that TSN may provide access to User data to our employees
                and service providers that have a legitimate need to access such information to
                provide their services to us. TSN, our employees, affiliates, partners, service
                providers, or agents involved in the handling, transmittal, and processing of User
                data will be required to maintain confidentiality of such data. TSN will not share
                User data with third parties other than described in these Terms or with the consent
                of you and/or the applicable User.
            </p>
            <h4 className={styles.h4}>F. Deletion of Data and Content Assets</h4>
            <p className={styles.p}>
                You may request that TSN delete User data in our possession at any time by providing
                a request in writing. We will verify the request source and shall use commercially
                reasonable efforts to comply with the request within 30 days. An individual user
                over the age of 18 or the parent of a user under the age of 18 seeking to access,
                modify, correct, or delete personal information connected to an educational or
                business account will be instructed to contact the educational institution or
                business to discuss their individual needs. TSN is not required to delete data that
                has been de-identified and/or anonymized to the point that it cannot reasonably
                identify a specific individual.
            </p>
            <h4 className={styles.h4}>G. Data Security and Breach Notification</h4>
            <p className={styles.p}>
                TSN has implemented operational, physical, and technical controls designed to secure
                the PII in TSN’s possession and control from unauthorized access, disclosure, and
                use. If we discover that an unauthorized party has accessed or disclosed PII related
                to your account (a &quot;<strong>Security Event</strong>&quot;) that has been
                collected or received through the Services, we will promptly notify you.
            </p>
            <p className={styles.p}>
                If the Security Event is due to the acts or omissions of TSN or its agents, we will
                make the appropriate notifications as required under applicable privacy laws. You
                shall be responsible for the timing, content, and method of any such
                legally-required notice and compliance with such laws as they relate to you and your
                Users.
            </p>
            <p className={styles.p}>
                If the Security Event is not caused by the acts or omissions of TSN or its agents,
                TSN will reasonably cooperate with your investigation of the Security Event, as
                requested in writing, at your reasonable expense, but TSN shall not indemnify you
                for costs associated with the Security Event.
            </p>

            <h2 className={styles.h2}>
                8. <u>User Content Change</u>
            </h2>
            <p className={styles.p}>
                You are solely responsible for any content that you or any of your Users create,
                upload, transmit, share, or display while using the Services.
            </p>
            <p className={styles.p}>
                TSN may now or in the future allow Users to upload, submit, post, display, provide,
                or otherwise make available content such as text, images, videos, animations, live
                feeds, social media feeds, audio productions, comments, questions, or other content
                or information. Any materials a User uploads, submits, creates, posts, displays,
                provides, or otherwise makes available on the Services is referred to as &quot;
                <strong>User Content.</strong>&quot;
            </p>
            <p className={styles.p}>
                User Content does not include unedited, unaltered content provided through the
                Services by TSN, such as TSN Global Content housed in the Global Library or TSN
                Premium Content housed in the Premium Library.
            </p>
            <p className={styles.p}>
                TSN makes no ownership claims over User Content. As between you and TSN, all User
                Content that your Users create, upload, display, provide or otherwise make available
                through the Services (&quot;<strong>Your User Content</strong>&quot;) remains yours.
                You are solely responsible for your Users&apos; User Content and ensuring all such
                User Content abides by current laws and regulations.
            </p>
            <h4 className={styles.h4}>A. Acknowledgement and Grant of Rights</h4>
            <p className={styles.p}>
                You must have the legal right to submit and display all of Your User Content. You
                represent, covenant, and agree, on behalf of all your Users, that your Users have
                all of the required rights and authorizations to submit, post, display, upload,
                disseminate, edit, or otherwise own and use Your User Content without violating any
                third party’s rights.
            </p>
            <p className={styles.p}>
                By using the Services, you hereby grant TSN a limited, non-exclusive, sublicensable,
                worldwide, royalty-free, perpetual, irrevocable, and transferable right and license
                to:
                <ul className={styles.ul}>
                    <li className={styles.li}>
                        use, host, copy, store, distribute, publicly perform and display, publish
                        (in whole or in part), modify, translate, syndicate, and create derivative
                        works from (such as changes TSN makes so that the content works better with
                        the Services) Your User Content as necessary to provide, improve and make
                        the Services available to you and your Users, including through any known
                        and future media formats/media channels in which the Services may be
                        distributed;
                    </li>
                    <li className={styles.li}>
                        use, modify, prepare derivative works, publish, distribute and sublicense
                        feedback without any compensation to you or any other party;
                    </li>
                    <li className={styles.li}>
                        use and disclose metrics and analytics regarding Your User Content in an
                        aggregate or another non-personally identifiable manner (including, for use
                        in improving the Services); and,
                    </li>
                    <li className={styles.li}>
                        use any de-identified User Content for any lawful purpose.
                    </li>
                </ul>
            </p>
            <h4 className={styles.h4}>B. Responsibility for User Content and TSN’s Rights</h4>
            <p className={styles.p}>
                You acknowledge and agree that TSN reserves the right, at any time and without prior
                notice, to pre-screen, reject, move, edit, delete, or remove any of Your User
                Content that TSN, in our sole discretion, considers to be objectionable for any
                reason, in violation of these Terms, or otherwise harmful to the Services or Users,
                consistent with applicable law.
            </p>
            <p className={styles.p}>
                Should you use third party sources, links, or applications to make Your User Content
                available through the Services, you acknowledge and agree that it is your sole
                responsibility to adhere to the terms and policies of those third party sources or
                applications.
            </p>
            <h4 className={styles.h4}>C. Links, Third-Party Content, and Third-Party Websites</h4>
            <p className={styles.p}>
                The Services may include content originating from and created by third parties,
                including information about third-party products and services, links to third-party
                websites that are not owned or controlled by TSN, and third-party applications,
                including, but not limited to, third-party applications that are integrated directly
                with the Services through application programming interfaces (each an &quot;
                <strong>API</strong>&quot;) (collectively, &quot;
                <strong>Third-Party Content and Websites</strong>&quot;).
            </p>
            <p className={styles.p}>
                Third-Party Content and Websites may allow Users to interact through and communicate
                with third parties. The inclusion of Third-Party Content and Websites on the
                Services does not imply our affiliation or endorsement of such Third-Party Content
                and Websites. You agree that TSN is not responsible for any such Third-Party Content
                and Websites, including the accuracy, integrity, quality, legality, usefulness,
                safety, privacy practices or intellectual property rights of or relating to such
                Third-Party Content and Websites, and you and your Users access such Third-Party
                Content and Websites at your or their own risk. TSN has no obligation to monitor
                such Third-Party Content and Third-Party Websites. You are solely responsible for
                any data or User Content that you share with or on Third Party Content and Websites,
                all of which are governed by their own terms and/or policies for such Third Party
                Content and Websites.
            </p>
            <p className={styles.p}>
                You understand and agree that your use of the Services may expose you to Third-Party
                Content and Websites, and that you use the Services at your own risk.
            </p>

            <h2 className={styles.h2}>
                9. <u>Copyright Complaints</u>
            </h2>
            <p className={styles.p}>
                TSN will take each alleged copyright infringement seriously and will comply with the
                Digital Millennium Copyright Act of 1998 (“DMCA”).
            </p>
            <p className={styles.p}>
                If you believe your copyrighted work has been used within the Services in a way that
                constitutes copyright infringement, please notify TSN’s Legal Department. For your
                complaint to be valid under the DMCA, you must provide the following information in
                writing:
            </p>
            <ul className={styles.ul}>
                <li className={styles.li}>
                    An electronic or physical signature of a person authorized to act on behalf of
                    the copyright owner;
                </li>
                <li className={styles.li}>
                    Identification of the copyrighted work that you claim has been infringed;
                </li>
                <li className={styles.li}>
                    Identification of the material that is claimed to be infringing and where it is
                    located on the Services;
                </li>
                <li className={styles.li}>
                    Information reasonably sufficient to permit TSN to contact you, such as your
                    address, phone number, and e-mail address;
                </li>
                <li className={styles.li}>
                    A statement that you have a good faith belief that use of the material in the
                    manner complained of is not authorized by the copyright owner, its agent, or
                    law; and,
                </li>
                <li className={styles.li}>
                    A statement, made under penalty of perjury, that the above information is
                    accurate, and that you are the copyright owner or are authorized to act on
                    behalf of the owner.
                </li>
            </ul>
            <br />
            <p className={styles.p}>
                The above information must be submitted to the following:
                <br />
                TSN Legal Department
                <br />
                PO Box 764
                <br />
                Blanco, TX 78606
                <br /> E-mail: legal@tsnvcs.com
            </p>
            <p className={styles.p}>
                UNDER FEDERAL LAW, IF YOU KNOWINGLY MISREPRESENT THAT ONLINE MATERIAL IS INFRINGING,
                YOU MAY BE SUBJECT TO CRIMINAL PROSECUTION FOR PERJURY AND CIVIL PENALTIES,
                INCLUDING MONETARY DAMAGES, COURT COSTS, AND ATTORNEYS&apos; FEES.
            </p>
            <p className={styles.p}>
                Please note that this procedure is exclusively for notifying TSN and its affiliates
                that your copyrighted material has been infringed. The preceding requirements are
                intended to comply with TSN’s rights and obligations under the DMCA, including 17
                U.S.C. §512(c), but do not constitute legal advice. It may be advisable to contact
                an attorney regarding your rights and obligations under the DMCA and other
                applicable laws.
            </p>

            <h2 className={styles.h2}>
                10. <u>Disputes and Releases</u>
            </h2>
            <p className={styles.p}>
                TSN is not responsible for the actions, content, information or data of third
                parties, including other TSN users that are not affiliated with you. You and your
                Users are solely responsible for your or their interactions with non-affiliated
                users of the Services and any other parties with whom you or your Users may interact
                through the Services. TSN reserves the right but has no obligation to become
                involved in any way with these disputes.
            </p>
            <p className={styles.p}>
                If you have a dispute with one or more non-affiliated users of the Services, you
                hereby release TSN (and our officers, directors, agents, subsidiaries, joint
                ventures, and employees) from any and all claims, demands, and damages (actual and
                consequential) of every kind and nature, known and unknown, arising out of or in any
                way connected with such disputes, including damages for loss of profits, goodwill,
                use, privacy, or data.
            </p>

            <h2 className={styles.h2}>
                11. <u>Termination and Suspension</u>
            </h2>
            <p className={styles.p}>
                You are responsible for managing your Users and may terminate their use of the
                Services at any time. TSN reserves the right to request additional information from
                you before executing any termination request.
            </p>
            <p className={styles.p}>
                You acknowledge that if you or any of your Users knowingly, intentionally, or
                negligently violate these Terms, TSN may suspend the User’s account until the
                violation is stopped. In the event that you fail to correct the violation after
                reasonable notice from TSN, we may terminate your license to the Services, in whole
                or in part, as necessary to stop the violation.
            </p>
            <p className={styles.p}>
                You acknowledge and agree that if you or any of your Users violate these Terms, TSN
                bears no responsibility for any damages or claims resulting from or in connection
                with such actions.
            </p>
            <p className={styles.p}>
                Upon termination of your license to the Services, your right and your Users’ rights
                to use the Services will immediately cease. All provisions of these Terms, which by
                their nature, should survive termination, shall survive termination, including
                without limitation, ownership provisions, warranty disclaimers, and limitations of
                liability.
            </p>

            <h2 className={styles.h2}>
                12. <u>Special Admonitions for International Use</u>
            </h2>
            <p className={styles.p}>
                Recognizing the global nature of the Internet, you agree to comply with and are
                solely responsible for ensuring compliance with all local laws, regulations, and
                rules in the jurisdiction(s) in which you reside, work, or use the Services. You
                agree to comply with all applicable laws regarding the transmission of data and
                content exported from or imported to the United States or the jurisdiction(s) in
                which you reside.
            </p>

            <h2 className={styles.h2}>
                13. <u>Additional Requirements by User Type</u>
            </h2>
            <h4 className={styles.h4}>A. Personnel, Faculty, Staff</h4>
            <p className={styles.p}>
                All Users must obtain the necessary approvals from their respective organizations
                and administrators prior to using the Services. Upon termination of employment,
                engagement, or contractual relationship with your organization, terminated Users
                must cease use of the Services and return and cease all login information in their
                possession.
            </p>
            <h4 className={styles.h4}>B. Students and Users Under the Age of 18</h4>
            <p className={styles.p}>
                Through an Administrator Account, you may grant permission to student Users or Users
                under the age of 18 to have User accounts and use the Services through your TSN
                account. You understand and acknowledge that Student Users and Users under the age
                of 18 are not authorized to use the Services through your TSN account unless you
                grant permission for them to use the Services.
            </p>
            <p className={styles.p}>
                By creating accounts for Student Users and Users under the age of 18, you
                acknowledge and agree that you have the authority to do so and that you are solely
                responsible (and hereby agree that TSN is not responsible) for meeting all legal and
                regulatory requirements for (i) providing any and all legally required notices and
                obtaining any necessary permissions, including all COPPA-related permissions and
                consents for children under 13 years of age to use the Services; and (ii) provide
                any legally required means for a parent or legal guardian to review or correct any
                Student Data in the Services.
            </p>
            <p className={styles.p}>
                Under no circumstances will TSN be liable if you fail to comply with all legal
                requirements related to granting students or other Users access to the Services
                through your TSN account.
            </p>

            <h2 className={styles.h2}>
                14. <u>Indemnity</u>
            </h2>
            <p className={styles.p}>
                To the extent permitted by applicable law, you agree to indemnify and hold harmless
                TSN, and its subsidiaries, affiliates, officers, agents, co-branders or other
                partners, and employees, from any claim, suit, action, costs, and damages (including
                reasonable attorney’s fees) arising from or related to: (i) any content that you or
                any of your Users submits, posts, transmits, or make available through the Services,
                including without limitation, User Content, (ii) any misuse of the Services by you
                or any of your Users, (iii) your connection to the Services, (iv) any violation of
                these Terms by you or any of your Users, (v) any violation of any applicable law or
                the rights of another person or entity by you or any of your Users, (vi) any
                negligent or other tortious conduct by your or any of your Users, or (vii) any third
                party&apos;s access and use of the Services using login credentials of yours or any
                of your Users. TSN reserves the right, at our own expense, to assume the exclusive
                defense and control of any matter for which you are required to indemnify us and you
                agree to cooperate with our defense of these claims.
            </p>

            <h2 className={styles.h2}>
                15. <u>Notifications and Modifications of Terms</u>
            </h2>
            <p className={styles.p}>
                TSN may make required and/or optional notifications via email notice, written or
                hard copy notice, or through posting of such notice in our Services, as determined
                by TSN in our sole discretion.
            </p>
            <p className={styles.p}>
                TSN may, in its sole discretion, modify or update these Terms at any time. TSN will
                notify you of any material changes to these Terms at least 30 days before such
                changes become effective, and your continued use of the Services following any such
                change will constitute your acceptance of the revised Terms. If you do not agree to
                any of the terms or to any future terms in a future revision, do not use or access
                (or continue to use) the Services.
            </p>

            <h2 className={styles.h2}>
                16. <u>Modification or Termination of Service</u>
            </h2>
            <p className={styles.p}>
                TSN reserves the right at any time, with or without notice, to modify, temporarily
                suspend, or permanently remove any feature, functionality, component, or
                availability of the Services. You agree that TSN shall not be liable to you or to
                any third party for any modification, suspension, or discontinuance of the Services.
                In the event of permanent discontinuance of the Services, TSN&apos;s liability is
                limited to the paid subscription price, proportional to the amount of time remaining
                on the subscription.
            </p>

            <h2 className={styles.h2}>
                17. <u>Disclaimers & Liability Limitations</u>
            </h2>
            <h4 className={styles.h4}>A. Warranty Disclaimer</h4>
            <p className={styles.p}>YOU EXPRESSLY UNDERSTAND AND AGREE THAT:</p>
            <p className={styles.p}>
                YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK. THE SERVICES ARE PROVIDED ON AN
                &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; BASIS. TSN EXPRESSLY DISCLAIMS ALL
                WARRANTIES AND CONDITIONS OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING, BUT
                NOT LIMITED TO (i) THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                PARTICULAR PURPOSE AND NON-INFRINGEMENT; (ii) THE WARRANTIES AGAINST HIDDEN OR
                LATENT DEFECTS AND NON-INFRINGEMENT, MISAPPROPRIATION OR VIOLATION OF ANY
                INTELLECTUAL PROPERTY OR PROPRIETARY RIGHTS OF ANY PERSON OR INSTITUTION; (iii) THE
                WARRANTIES ARISING THROUGH COURSE OF DEALING OR USAGE IN TRADE; AND (iv) THE
                WARRANTIES RELATING TO THE ACCURACY, RELIABILITY, CORRECTNESS, OR COMPLETENESS OF
                DATA OR CONTENT (INCLUDING USER CONTENT) MADE AVAILABLE ON THE SERVICES OR OTHERWISE
                BY TSN.
            </p>
            <p className={styles.p}>
                TSN MAKES NO WARRANTY OR CONDITION THAT (i) THE SERVICES WILL MEET YOUR
                REQUIREMENTS, (ii) THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, OR
                ERROR-FREE, (iii) THE RESULTS THAT MAY BE OBTAINED FROM THE USE OF THE SERVICES WILL
                BE ACCURATE OR RELIABLE, (iv) THE QUALITY OF ANY PRODUCTS, SERVICES, INFORMATION, OR
                OTHER MATERIAL PURCHASED OR OBTAINED BY YOU THROUGH THE SERVICES WILL MEET YOUR
                EXPECTATIONS, AND (V) ANY ERRORS IN THE SOFTWARE OR CONTENT WILL BE CORRECTED.
            </p>
            <p className={styles.p}>
                ANY MATERIAL DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE USE OF THE SERVICES IS
                DONE AT YOUR OWN DISCRETION AND RISK AND THAT YOU WILL BE SOLELY RESPONSIBLE FOR ANY
                DAMAGE TO YOUR COMPUTER SYSTEM OR LOSS OF DATA THAT RESULTS FROM THE DOWNLOAD OF ANY
                SUCH MATERIAL.
            </p>
            <p className={styles.p}>
                NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED BY YOU FROM TSN OR
                THROUGH OR FROM THE SERVICES SHALL CREATE ANY WARRANTY OR CONDITION NOT EXPRESSLY
                STATED IN THESE TERMS.
            </p>
            <h4 className={styles.h4}>B. Limitation of Liability</h4>
            <p className={styles.p}>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL TSN, ITS
                AFFILIATES, AGENTS, DIRECTORS, EMPLOYEES, SUPPLIERS OR LICENSORS BE LIABLE FOR ANY
                DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES, INCLUDING
                BUT NOT LIMITED TO, DAMAGES FOR LOSS OF PROFITS, GOODWILL, USE, DATA OR OTHER
                INTANGIBLE LOSSES (EVEN IF TSN HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES),
                RESULTING FROM: (i) THE USE OR THE INABILITY TO USE THE SERVICES; (ii) THE COST OF
                PROCUREMENT OF SUBSTITUTE GOODS AND SERVICES RESULTING FROM ANY GOODS, DATA,
                INFORMATION OR SERVICES PURCHASED OR OBTAINED OR MESSAGES RECEIVED OR TRANSACTIONS
                ENTERED INTO THROUGH OR FROM THE SERVICES; (iii) UNAUTHORIZED ACCESS TO OR
                ALTERATION OF YOUR TRANSMISSIONS, CONTENT, OR DATA; (iv) STATEMENTS OR CONDUCT OF
                ANY THIRD PARTY ON THE SERVICES; OR (v) ANY OTHER MATTER RELATING TO THE SERVICES.
            </p>
            <p className={styles.p}>
                In no event shall TSN or its subsidiaries, parent companies, affiliates, licensors,
                contractors, employees, officers, directors, agents or third-party partners&apos;
                total liability to you for all damages, losses, and causes of action arising out of
                or relating to these Terms or your use of the Services (whether in contract, tort,
                warranty or otherwise) exceed the amount paid by you, if any, for accessing the
                Services during the 12-month period immediately preceding your claim or one hundred
                dollars ($100), whichever is greater.
            </p>
            <h4 className={styles.h4}>C. Exclusions and Limitations</h4>
            <p className={styles.p}>
                SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES AND CONDITIONS
                OR THE LIMITATION OR EXCLUSION OF LIABILITY FOR CERTAIN CLAIMS OR DAMAGES SUCH AS
                INCIDENTAL OR CONSEQUENTIAL DAMAGES. ACCORDINGLY, THE DISCLAIMERS, EXCLUSIONS AND
                LIMITATIONS OF LIABILITY UNDER THIS AGREEMENT WILL NOT APPLY TO THE EXTENT
                PROHIBITED BY APPLICABLE LAW.
            </p>

            <h2 className={styles.h2}>
                18. <u>Governing Law and Venue</u>
            </h2>
            <p className={styles.p}>
                You agree that: (i) the Services shall be deemed solely based in Texas; and (ii) the
                Services shall be deemed a passive one that does not give rise to personal
                jurisdiction over us, either specific or general, in jurisdictions other than Texas.
                This Agreement shall be governed by the internal substantive laws of the State of
                Texas, without respect to its conflict of laws principles. The parties acknowledge
                that these Terms evidence a transaction involving interstate commerce.
                Notwithstanding the preceding sentences with respect to the substantive law, any
                arbitration conducted pursuant to these Terms shall be governed by the Federal
                Arbitration Act (9 U.S.C. §§ 1-16) (the &quot;<strong>FAA</strong>&quot;). These
                Terms are a contract for the provision of services and not a contract for the sale
                of goods. The provisions of the Uniform Commercial Code (UCC), the Uniform Computer
                Information Transaction Act (UCITA), or any substantially similar legislation as may
                be enacted, shall not apply to these Terms. If you are located outside of the
                territory of the United States, the parties agree that the United Nations Convention
                on Contracts for the International Sale of Goods shall not govern this Agreement or
                the rights and obligations of the parties under these Terms.
            </p>
            <p className={styles.p}>
                You agree to submit to the personal jurisdiction of the federal and state courts
                located in Blanco County, Texas, for any actions for which we retain the right to
                seek injunctive or other equitable relief in a court of competent jurisdiction to
                prevent the actual or threatened infringement, misappropriation or violation of our
                copyrights, trademarks, trade secrets, patents, or other intellectual property or
                proprietary rights, as set forth in the Arbitration provision below, including any
                provisional relief required to prevent irreparable harm. You agree that Blanco
                County, Texas is the proper forum for any appeals of an arbitration award or for
                trial court proceedings if the arbitration provision below is found to be
                unenforceable.
            </p>

            <h2 className={styles.h2}>
                19. <u>Arbitration Agreement & Class Action/Jury Trial Waiver</u>
            </h2>
            <p className={styles.p}>
                <strong>
                    READ THIS SECTION CAREFULLY BECAUSE IT REQUIRES THE PARTIES TO ARBITRATE THEIR
                    DISPUTES AND LIMITS THE MANNER IN WHICH YOU CAN SEEK RELIEF FROM TSN.
                </strong>
            </p>
            <h4 className={styles.h4}>A. Applicability</h4>
            <p className={styles.p}>
                TSN and you (&quot;<strong>the parties</strong>&quot; or &quot;
                <strong>both parties</strong>&quot;) agree that to the extent permitted by law, all
                Disputes (as defined below), including Enforceability Disputes (as defined below)
                that cannot be resolved through reasonable, good faith efforts to cooperate and
                achieve a mutually satisfactory resolution will be resolved by binding arbitration
                on an individual basis, except that the parties are not required to arbitrate IP
                Disputes (as defined below). Any court with jurisdiction over the parties may
                enforce the arbitrator&apos;s award. Notwithstanding the foregoing, either TSN or
                you may bring an individual action in small claims court.
            </p>
            <p className={styles.p}>
                To the extent permitted by Law, any Dispute must be filed within one year in an
                arbitration proceeding. The one-year period begins when the notice of Dispute first
                could be filed. If a Dispute is not filed within one year, it is permanently barred.
            </p>
            <p className={styles.p}>
                If the arbitration provision in this Section is found to be unenforceable or
                invalid, the parties agree that any suit or proceeding arising out of or relating to
                these Terms will be brought in the federal or state courts, as applicable, in Blanco
                County, Texas, and each party irrevocably submits to the jurisdiction and venue of
                such courts.
            </p>
            <ul className={styles.ul}>
                <li className={styles.li}>
                    A &quot;<strong>Dispute</strong>&quot; means a dispute, claim or controversy
                    arising out of or relating to the Terms, or the Services; or whether that
                    dispute is (1) based on past, present or future events and includes claims and
                    disputes that arose between the parties before the effective date of these
                    Terms; and (2) in tort, contract, warranty, state, regulation, or other legal or
                    equitable basis.
                </li>
                <li className={styles.li}>
                    An &quot;<strong>Enforceability Dispute</strong>&quot; means a Dispute relating
                    to the interpretation, applicability, or enforceability of this Arbitration
                    Agreement, including the formation of the contract, the arbitrability of any
                    Dispute, and any claim that all or any part of this agreement is void or
                    voidable.
                </li>
                <li className={styles.li}>
                    An &quot;<strong>IP Dispute</strong>&quot; means a Dispute relating to the
                    ownership or enforcement of intellectual property rights.
                </li>
            </ul>
            <h4 className={styles.h4}>B. Federal Arbitration Act</h4>
            <p className={styles.p}>
                TSN and you agree that these Terms affect interstate commerce and that the FAA
                governs the interpretation and enforcement of these arbitration provisions.
            </p>
            <h4 className={styles.h4}>C. Initiating a Dispute</h4>
            <p className={styles.p}>
                To initiate a Dispute, a party must first give the other party written notice of the
                Dispute which must include a brief written statement containing: (a) the name,
                address, and contact information of the party giving notice; (b) the facts giving
                rise to the Dispute; and (c) the relief requested.
            </p>
            <p className={styles.p}>
                You must send any such notice to TSN by email to legal@tsnvcs.com AND by mail to
                TSN, LLC, PO Box 26481, Austin, TX 78755. If TSN has your contact information, TSN
                will send any such notice to you by the U.S. Mail and to the email address on file.
            </p>
            <p className={styles.p}>
                The parties must first attempt to resolve any Disputes through informal negotiation
                within 30 days from the date that any notice of a Dispute is sent. The parties shall
                use reasonable, good faith, efforts to settle any Dispute through consultation and
                good faith negotiations. After 30 days, either party may resort to the other
                alternatives described in this Section 19. Notwithstanding the foregoing, the notice
                and 30-day negotiation period required by this paragraph shall not apply to IP
                Disputes, claims of piracy or unauthorized use of the Services which will be brought
                in the federal or state courts, as applicable, in Blanco County, Texas, and each
                party irrevocably submits to the jurisdiction and venue of such courts.
            </p>
            <h4 className={styles.h4}>D. Arbitration Rules and Procedures</h4>
            <p className={styles.p}>
                To the extent permitted by law, TSN and you agree that all Disputes that cannot be
                resolved through reasonable, good faith efforts to cooperate and achieve a mutually
                satisfactory resolution will be resolved through binding arbitration on an
                individual basis in Blanco County, Texas, U.S.A. The arbitrator’s award shall be
                binding and may be entered as a judgment in a court of competent jurisdiction. If
                you are a U.S. public and accredited educational institution and your consent to
                binding arbitration is expressly prohibited by the laws of the state within the U.S.
                in which you are domiciled, then this subsection 19.D. is hereby waived.
            </p>
            <p className={styles.p}>
                The arbitration shall be conducted by a single arbitrator, governed by JAMS pursuant
                to its Comprehensive Arbitration Rules & Procedures (collectively, &quot;JAMS
                Rules&quot;), as modified by these Terms, and administered by JAMS. The JAMS Rules
                and fee information are available at <a href={"www.jamsadr.org"}>www.jamsadr.org</a>{" "}
                or by calling JAMS at 1-800-352-5267.
            </p>
            <p className={styles.p}>
                The decision of the arbitrator will be in writing and binding and conclusive on you
                and TSN, and judgment to enforce the decision may be entered by any court of
                competent jurisdiction.TSN and you agree that dispositive motions, including without
                limitation motions to dismiss and motions for summary judgment, will be allowed in
                the arbitration. The arbitrator must follow these Terms and can award the same
                damages and relief as a court, including injunctive or other equitable relief and
                attorney’s fees.
            </p>
            <p className={styles.p}>
                If any clause within this subsection 19.D is found to be illegal or unenforceable,
                that specific clause will be severed from this Section, and the remainder of its
                provisions will be given full force and effect.
            </p>
            <h4 className={styles.h4}>E. Waiver of Class Action/Jury Trial</h4>
            <p className={styles.p}>
                WITH RESPECT TO ALL PERSONS AND ENTITIES, REGARDLESS OF WHETHER THEY HAVE OBTAINED
                OR USED THE SERVICES FOR PERSONAL, COMMERCIAL OR OTHER PURPOSES, ALL CLAIMS MUST BE
                BROUGHT IN THE PARTIES&apos; INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS
                MEMBER IN ANY PURPORTED CLASS ACTION, COLLECTIVE ACTION, PRIVATE ATTORNEY GENERAL
                ACTION OR OTHER REPRESENTATIVE PROCEEDING. THIS WAIVER APPLIES TO CLASS ARBITRATION,
                AND, UNLESS WE AGREE OTHERWISE, THE ARBITRATOR MAY NOT CONSOLIDATE MORE THAN ONE
                PERSON&apos;S CLAIMS. YOU AGREE THAT, BY ENTERING INTO THIS AGREEMENT, YOU AND TSN
                ARE EACH WAIVING THE RIGHT TO A TRIAL BY JURY OR TO PARTICIPATE IN A CLASS ACTION,
                COLLECTIVE ACTION, PRIVATE ATTORNEY GENERAL ACTION, OR OTHER REPRESENTATIVE
                PROCEEDING OF ANY KIND.
            </p>
            <h4 className={styles.h4}>F. Confidentiality</h4>
            <p className={styles.p}>
                All aspects of the arbitration, including without limitation, the record of the
                proceeding, are confidential and shall not be open to the public, except (a) to the
                extent both Parties agree otherwise in writing, (b) as may be appropriate in any
                subsequent proceedings between the Parties, or (c) as may otherwise be appropriate
                in response to a governmental agency or legal process, provided that the Party upon
                whom such process is served shall give immediate notice of such process to the other
                Party and afford the other Party an appropriate opportunity to object to such
                process.
            </p>

            <h2 className={styles.h2}>
                20. <u>General Terms</u>
            </h2>
            <h4 className={styles.h4}>A. Severability</h4>
            <p className={styles.p}>
                If any provision of these Terms is invalid or unenforceable, such provision shall be
                changed and interpreted to accomplish the objectives of such provision to the
                greatest extent possible under applicable laws and the remaining provisions will
                continue in full force and effect.
            </p>
            <h4 className={styles.h4}>B. No Waiver</h4>
            <p className={styles.p}>
                A failure by TSN to enforce any provisions of the Terms or respond to a breach by
                you or anyone else shall not in any way waive TSN&apos;s right to enforce
                subsequently any terms or conditions of the Terms or to act with respect to similar
                breaches.
            </p>
            <h4 className={styles.h4}>C. Assignment</h4>
            <p className={styles.p}>
                You must not assign these Terms or any rights or obligations herein without the
                prior written consent of TSN and any attempted assignment in contravention of this
                provision is null and void and of no force or effect. TSN has the right to assign
                these Terms, and any of its rights or obligations herein. These Terms are binding
                upon each party and its respective successors, heirs, trustees, administrators,
                executors and permitted assigns.
            </p>
            <h4 className={styles.h4}>D. Relationship of the Parties</h4>
            <p className={styles.p}>
                The parties are independent contractors and not employees, partners, agents, or
                joint venturers of the other and have no employment relationship whatsoever. No
                party will make any commitment, by contract or otherwise, binding upon the other
                party or represent that it has any authority to do so. These Terms are a
                non-exclusive agreement. You agree that, except as otherwise expressly provided in
                these Terms, there shall be no third party beneficiaries to these Terms.
            </p>
            <h4 className={styles.h4}>E. Entire Agreement</h4>
            <p className={styles.p}>
                These Terms are the entire agreement of the parties and supersede all previous or
                contemporaneous agreements between the parties relating to its subject matter. In
                connection with your use of the Services, you may be asked to consent to policies or
                terms and conditions in addition to these Terms. Any separate terms will not vary or
                replace these Terms regarding any use of the Services, unless otherwise expressly
                stated.
            </p>
            <h4 className={styles.h4}>F. Marketing Communications</h4>
            <p className={styles.p}>
                You agree to receive marketing communications from TSN, consistent with applicable
                law, including announcements about upcoming professional development training,
                events, and new products or services.
            </p>
            <h4 className={styles.h4}>G. Section Titles</h4>
            <p className={styles.p}>
                The action titles in these Terms are for convenience only and have no legal or
                contractual effect.
            </p>
            <h3 className={styles.h3}>
                <strong>Last Updated: January 1, 2025</strong>
            </h3>
        </article>
    )
})
